package xim.poc.game.configuration.v0.tactics

import xim.math.Vector2f
import xim.math.Vector3f
import xim.poc.*
import xim.poc.audio.AudioManager
import xim.poc.audio.SystemSound
import xim.poc.browser.GameKey
import xim.poc.browser.GameKey.*
import xim.poc.browser.GamepadKey
import xim.poc.browser.GamepadManager
import xim.poc.browser.WheelDirection
import xim.poc.camera.Camera
import xim.poc.camera.CameraReference
import xim.poc.camera.PolarCamera
import xim.poc.game.*
import xim.poc.game.InputThrottler.admit
import xim.poc.game.UiStateHelper.keyPressed
import xim.poc.game.UiStateHelper.keyPressedOrRepeated
import xim.poc.game.UiStateHelper.pushState
import xim.poc.game.event.Event
import xim.poc.gl.ByteColor
import xim.poc.gl.Color
import xim.poc.ui.*
import xim.resource.UiMenuCursorKey
import xim.resource.ZoneObject
import xim.util.FrameTimer
import xim.util.PI_f
import xim.util.interpolate
import kotlin.math.*
import kotlin.time.Duration.Companion.milliseconds

class Highlight(val minColor: Color, val maxColor: Color) {

    companion object {
        val redHighlight = Highlight(minColor = Color(1.2f, 0.8f, 0.8f, 1f), maxColor = Color(1.5f, 0.8f, 0.8f, 1f))
        val greenHighlight = Highlight(minColor = Color(0.8f, 1.2f, 0.8f, 1f), maxColor = Color(0.8f, 1.5f, 0.8f, 1f))
        val blueHighlight = Highlight(minColor = Color(0.8f, 0.8f, 1.2f, 1f), maxColor = Color(0.8f, 0.8f, 1.5f, 1f))
        val yellowHighlight = Highlight(minColor = Color(1.1f, 1.2f, 0.8f, 1f), maxColor = Color(1.4f, 1.5f, 0.8f, 1f))
        val aquaHighlight = Highlight(minColor = Color(0.8f, 1.3f, 1.2f, 1f), maxColor = Color(0.8f, 1.6f, 1.5f, 1f))
    }

    fun interpolate(d: Float): Color {
        return Color.interpolate(minColor, maxColor, d)
    }
}

class CycleContext(val numUnits: Int, val current: Int)

class StatusContext(var index: Int)

class TacticsUi(val match: TacticsMatchInstance) {

    var context: TacticsUiContext = TacticsNoopContext
        private set

    var skillSpeculation: List<Event>? = null

    private var cycleContext: CycleContext? = null
    private var statusContext: StatusContext? = null

    private val worldCursorPosition = Vector3f()
    private var cursorYOffset = 0f

    private var stolenCamera: Camera? = null
    private var matchCamera: PolarCamera? = null

    private var highlightInterpolation = 1f
    private var highlightDelta = 0.02f

    var removedRamp = false
        private set

    private var desiredCameraPosition = 3
    private var currentCameraPosition = desiredCameraPosition
    private var cameraAngleT = 0f
    private var cameraRadius = 6f

    private val scrollThrottle = FrameTimer(100.milliseconds)

    private val statusUiState = UiState(
        onPushed = { statusContext = StatusContext(0); true },
        onPopped = { statusContext = null },
        locksMovement = true,
        enableCursorMovementKeys = true,
        locksCursor = true,
        drawParent = true,
    ) {
        val currentIndex = statusContext?.index ?: 0
        val statuses = match.getHighlightedUnit()?.getStatusEffects() ?: emptyList()

        val nextIndex = when (UiStateHelper.getDirectionalInput(throttle = 160.milliseconds)) {
            UiMenuCursorKey.Right -> (currentIndex + 1).mod(statuses.size)
            UiMenuCursorKey.Left -> (currentIndex - 1).mod(statuses.size)
            else -> currentIndex
        }

        statusContext = StatusContext(nextIndex)

        if (keyPressed(UiExit)) {
            UiStateHelper.popState(SystemSound.MenuClose)
        }

        true
    }

    val uiState = UiState(
        onPushed = this::stealCamera,
        onPopped = this::returnCamera,
        additionalDraw = { draw() },
        locksMovement = true,
        enableCursorMovementKeys = true,
        locksCursor = true,
    ) {
        when (val matchState = match.matchState) {
            is TacticsMatchConcluded -> return@UiState false
            is TacticsMatchUnitTurn -> if (matchState.isEnemyTurn() || matchState.hasSubmittedAction()) { return@UiState false }
            is TacticsMatchSetup -> {}
        }

        if (keyPressed(UiEnter)) {
            processKey(UiEnter)
        } else if (keyPressed(UiExit)) {
            processKey(UiExit)
        } else if (keyPressedOrRepeated(TargetCycle)) {
            processKey(TargetCycle)
        } else if (keyPressed(UiContext)) {
            val unit = match.getHighlightedUnit()
            if (unit != null && unit.getStatusEffects().isNotEmpty()) {
                pushState(statusUiState, SystemSound.MenuSelect)
            }
        } else {
            val uiDirection = UiStateHelper.getDirectionalInput(throttle = 150.milliseconds)
            if (uiDirection != null) { processKey(uiDirection) }
        }

        false
    }

    fun push() {
        pushState(uiState)
        activateContext(TacticsTowerContext(this))
    }

    fun cleanUp() {
        clearPanelHighlights()
        UiStateHelper.clear()
    }

    fun reset() {
        val currentTurn = match.getCurrentTurn()
        if (currentTurn != null) {
            activateContext(TacticsBrowseContext(this, currentTurn))
        } else if (match.isSetupPhase()) {
            activateContext(TacticsSummonContext(this))
            match.setCursor(PanelPosition(2, 0))
        } else {
            throw IllegalStateException("Cannot reset UI")
        }
    }

    fun activateContext(context: TacticsUiContext) {
        this.context = context
        context.onActivated()
    }

    fun update(elapsedFrames: Float) {
        clearPanelHighlights()
        context.applyPanelHighlights()

        maybeRemoveRamp()

        detectChatLogScrollInput(elapsedFrames)
        detectCameraChangeInput()
        updateCameraAngle(elapsedFrames)

        highlightInterpolation = (highlightInterpolation + highlightDelta * elapsedFrames).coerceIn(0f, 1f)
        if (highlightInterpolation == 0f || highlightInterpolation == 1f) { highlightDelta *= -1f }

        if (match.getHighlightedUnit() == null) { cycleContext = null }

        updateCursorGraphic(elapsedFrames)
    }

    private fun updateCursorGraphic(elapsedFrames: Float) {
        val desiredPosition = getCursorWorldPosition()

        val distance = Vector3f.distance(worldCursorPosition, desiredPosition)
        if (distance < 1e-4f) { return }

        val speedBonus = (distance / 4f).coerceAtLeast(1f)

        val movementAmount = 0.6f * speedBonus * elapsedFrames
        if (distance < movementAmount) {
            worldCursorPosition.copyFrom(desiredPosition)
            return
        }

        val direction = (desiredPosition - worldCursorPosition).normalizeInPlace()
        worldCursorPosition.addInPlace(direction * movementAmount)
    }

    fun highlight(highlight: Highlight): Color {
        return highlight.interpolate(highlightInterpolation)
    }

    fun getSelectingCost(actorState: ActorState): Int {
        val current = context

        if (current is TacticsSkillTargetSelect) {
            if (actorState.id != current.turn.unit.id) { return 0 }
            return getActorSkillCost(actorState, current.skill).baseCost.value
        } else if (current is TacticsPositioningTargetSelect) {
            if (actorState.id != current.turn.unit.id) { return 0 }
            return current.skill.tpCost()
        }

        if (current !is TacticsSkillSelect) {
            return 0
        }

        if (actorState.id != current.unit.id) { return 0 }

        return when(val skill = current.getHoveredSkill()) {
            is PositionChoice -> skill.positionSkill.tpCost()
            is SkillChoice -> getActorSkillCost(actorState, skill.skillId).baseCost.value
            WaitChoice -> 200
            null -> 0
        }
    }

    private fun processKey(gameKey: GameKey) {
        when (gameKey) {
            UiEnter -> context.handleEnter()
            UiExit -> context.handleExit()
            TargetCycle -> triggerCursorCycle()
            else -> return
        }
    }

    private fun processKey(uiDirection: UiMenuCursorKey) {
        val view = CameraReference.getInstance().getViewVector()
        val angle = atan2(view.z, view.x)

        if (abs(angle) > 0.75f * PI_f) {
            when (uiDirection) {
                UiMenuCursorKey.Right -> match.adjustCursor(0, 1)
                UiMenuCursorKey.Left -> match.adjustCursor(0, -1)
                UiMenuCursorKey.Up -> match.adjustCursor(-1, 0)
                UiMenuCursorKey.Down -> match.adjustCursor(1, 0)
            }
        } else if (angle > 0.25f * PI_f) {
            when (uiDirection) {
                UiMenuCursorKey.Right -> match.adjustCursor(1, 0)
                UiMenuCursorKey.Left -> match.adjustCursor(-1, 0)
                UiMenuCursorKey.Up -> match.adjustCursor(0, 1)
                UiMenuCursorKey.Down -> match.adjustCursor(0, -1)
            }
        } else if (angle > -0.25f * PI_f) {
            when (uiDirection) {
                UiMenuCursorKey.Right -> match.adjustCursor(0, -1)
                UiMenuCursorKey.Left -> match.adjustCursor(0, 1)
                UiMenuCursorKey.Up -> match.adjustCursor(1, 0)
                UiMenuCursorKey.Down -> match.adjustCursor(-1, 0)
            }
        } else {
            when (uiDirection) {
                UiMenuCursorKey.Right -> match.adjustCursor(-1, 0)
                UiMenuCursorKey.Left -> match.adjustCursor(1, 0)
                UiMenuCursorKey.Up -> match.adjustCursor(0, -1)
                UiMenuCursorKey.Down -> match.adjustCursor(0, 1)
            }
        }
    }

    private fun detectChatLogScrollInput(elapsedFrames: Float) {
        scrollThrottle.update(elapsedFrames)
        if (!scrollThrottle.isReady()) { return }

        GamepadManager.getRightAxisInput()?.let {
            if (it.y > 0.5f) {
                ChatLog.scroll(WheelDirection.Down)
                scrollThrottle.reset()
            }
            if (it.y < -0.5f) {
                ChatLog.scroll(WheelDirection.Up)
                scrollThrottle.reset()
            }
        }
    }

    private fun detectCameraChangeInput() {
        GamepadManager.getLeftAxisInput()?.let {
            if (it.x > 0.5f && admit(MoveRight, 150.milliseconds)) { processKey(UiMenuCursorKey.Right) }
            if (it.x < -0.5f && admit(MoveLeft, 150.milliseconds)) { processKey(UiMenuCursorKey.Left) }
            if (it.y > 0.5f && admit(MoveBackward, 150.milliseconds)) { processKey(UiMenuCursorKey.Down) }
            if (it.y < -0.5f && admit(MoveForward, 150.milliseconds)) { processKey(UiMenuCursorKey.Up) }
        }

        GamepadManager.getRightAxisInput()?.let {
            if (it.x > 0.7f) { processCameraAngleChange(1) }
            if (it.x < -0.7f) { processCameraAngleChange(-1) }
        }

        if (keyPressedOrRepeated(Autorun)) { processCameraAngleChange(-1) }

        if (keyPressed(CamZoomIn) || GamepadManager.isPressed(GamepadKey.L2)) {
            processCameraZoomChange(-1)
        } else if (keyPressed(CamZoomOut) || GamepadManager.isPressed(GamepadKey.R2)) {
            processCameraZoomChange(1)
        }

        ClickHandler.registerScrollListener(position = Vector2f.ZERO, size = Vector2f(Float.MAX_VALUE, Float.MAX_VALUE), priority = -1) {
            if (it.direction == WheelDirection.Up && admit(CamZoomIn, duration = 150.milliseconds)) {
                processCameraZoomChange(-1)
                true
            } else if (it.direction == WheelDirection.Down && admit(CamZoomOut, duration = 150.milliseconds)) {
                processCameraZoomChange(1)
                true
            } else {
                false
            }
        }
    }

    private fun processCameraAngleChange(sign: Int) {
        if (currentCameraPosition != desiredCameraPosition) { return }
        cameraAngleT = 0f
        desiredCameraPosition = (desiredCameraPosition + sign).mod(4)
        AudioManager.playSystemSoundEffect(SystemSound.ChangePoV)
    }

    private fun processCameraZoomChange(sign: Int) {
        cameraRadius = (cameraRadius + 2f * sign).coerceIn(4f, 14f)
        matchCamera?.adjustRadius(min = cameraRadius, max = cameraRadius)
    }

    private fun updateCameraAngle(elapsedFrames: Float) {
        val currentRadius = matchCamera?.getCurrentRadius() ?: 10f
        val theta = if (currentRadius <= 10f) {
            PI_f / 1.5
        } else {
            val adjustment = (currentRadius - 10f)/10f
            PI_f / (1.5 - adjustment).coerceIn(1.3, 1.5)
        }
        matchCamera?.adjust(theta = theta)

        if (currentCameraPosition == desiredCameraPosition) { return }
        val startAngle = currentCameraPosition * PI_f/2

        val endAngle = if (desiredCameraPosition == 0 && currentCameraPosition == 3) {
            2 * PI_f
        } else if (desiredCameraPosition == 3 && currentCameraPosition == 0) {
            -PI_f/2
        } else {
            desiredCameraPosition * PI_f/2
        }

        cameraAngleT = (cameraAngleT + 0.05f * elapsedFrames).coerceIn(0f, 1f)
        if (cameraAngleT >= 1f) { currentCameraPosition = desiredCameraPosition }

        val angle = startAngle.interpolate(endAngle, cameraAngleT)
        matchCamera?.adjust(phi = angle.toDouble())
    }

    private fun triggerCursorCycle() {
        if (context !is TacticsBrowseContext) { return }

        val units = match.getUnits()
        val currentCycleContext = cycleContext

        val next = if (currentCycleContext == null || currentCycleContext.numUnits != units.size) {
            0
        } else {
            (currentCycleContext.current + 1) % units.size
        }

        cycleContext = CycleContext(units.size, next)
        val nextPosition = units[next].getCursorPosition()

        if (match.cursorPosition == nextPosition && units.size > 1) {
            triggerCursorCycle()
            return
        }

        match.setCursor(units[next].getCursorPosition())
        AudioManager.playSystemSoundEffect(SystemSound.TargetCycle)
    }

    private fun clearPanelHighlights() {
        for (outer in panelZoneObjectIdLayout) {
            for (inner in outer) {
                val hoverObject = SceneManager.getCurrentScene().getMainArea().getZoneResource().zoneObj[inner]
                hoverObject.mask = Color.NO_MASK
            }
        }
    }

    fun draw() {
        drawCursor()
        if (UiStateHelper.isFocus(uiState) || UiStateHelper.isFocus(statusUiState)) { drawUnitSummary() }
        drawSkillSpeculation()
    }

    fun drawGauges() {
        match.getUnits().forEach { drawGauge(it) }
    }

    private fun drawGauge(actorState: ActorState) {
        val actor = ActorManager[actorState.id] ?: return

        val normalizedScreenSpace = (actor.displayPosition).toScreenSpace() ?: return

        val sss = MainTool.platformDependencies.screenSettingsSupplier
        val screenSpace = Vector2f(normalizedScreenSpace.x * sss.width, normalizedScreenSpace.y * sss.height)

        val scale = computeScale(actor.displayPosition)
        if (scale < 0.5f) { return }

        drawBattleGauge(actorState, screenSpace, Vector2f(scale, scale))
    }

    private fun drawBattleGauge(actor: ActorState, position: Vector2f, scale: Vector2f) {
        val hpScale = actor.getHp().toFloat() / actor.getMaxHp()
        val tpScale = (actor.getTp().toFloat() / 1000f).coerceIn(0f, 1f)

        val offset = position - Vector2f(60f, 10f) * scale

        UiElementHelper.drawUiElement(lookup = "menu    windowps", index = 98, position = Vector2f(51f, 21f) * scale + offset, scale = Vector2f(tpScale, 1f) * scale, color = Color(0.25f, 1f, 4f, 1f), disableGlobalScale = true)
        UiElementHelper.drawUiElement(lookup = "menu    windowps", index = 39, position = Vector2f(48f, 21f) * scale + offset, scale = scale, disableGlobalScale = true)

        UiElementHelper.drawUiElement(lookup = "menu    windowps", index = 97, position = Vector2f(28f, 12f) * scale + offset, scale = Vector2f(hpScale, 1f) * scale, disableGlobalScale = true)
        UiElementHelper.drawUiElement(lookup = "menu    windowps", index = 38, position = Vector2f(25f, 12f) * scale + offset, scale = scale, disableGlobalScale = true)

        val nameColor = if (actor.type == ActorType.Enemy) { ShiftJis.colorInvalid } else { ShiftJis.colorWhite }
        val name = if (actor.name.length > 8) { actor.name.take(8) + "..." } else { actor.name }
        UiElementHelper.drawString(text = "${nameColor}${name}", offset = Vector2f(4f, 4f) * scale + offset, Font.FontShp, scale = scale, disableGlobalScale = true)

        UiElementHelper.drawString(text = "${actor.getTp()}", offset = Vector2f(98f, 16f) * scale + offset, scale = scale, font = Font.FontShp, color = ByteColor.alpha75, alignment = TextAlignment.Right, disableGlobalScale = true)
        UiElementHelper.drawString(text = "${actor.getHp()}", offset = Vector2f(88f, 6f) * scale + offset, scale = scale, font = Font.FontShp, alignment = TextAlignment.Right, disableGlobalScale = true)

        val highlighted = match.getHighlightedUnit()?.id == actor.id

        actor.getStatusEffects().forEachIndexed { i, statusEffect ->
            val statusPosition = Vector2f(4f + i * 16f, 24f) * scale + offset

            if (highlighted && i == statusContext?.index) {
                UiElementHelper.drawUiElement(lookup = "anc     anc_regi", index = UiElementHelper.currentCursorIndex(6), position = statusPosition)
                ToolTipHelper.drawUiSpaceToolTip(statusPosition + Vector2f(4f, 22f), StatusEffectUi.getToolTipMessage(actor, statusEffect))
            }

            UiElementHelper.drawStatusEffect(statusEffect, statusPosition, scale = scale, disableGlobalScale = true)
            StatusEffectUi.addToolTip(actor, statusEffect, statusPosition, scale)
        }
    }

    private fun drawCursor() {
        val actorId = match.getHighlightedUnit()?.id ?: match.getHighlightedTower()?.id
        val actor = ActorManager[actorId]
        val desiredYOffset = (actor?.getScale() ?: 1f) * (actor?.actorModel?.getSkeleton()?.resource?.size?.y ?: 0f)

        val difference = abs(desiredYOffset - cursorYOffset)
        if (difference <= 0.1f) {
            cursorYOffset = desiredYOffset
        } else {
            cursorYOffset += sign(desiredYOffset - cursorYOffset) * 0.15f
        }

        val cursorPos = Vector3f(worldCursorPosition)
        cursorPos.y += cursorYOffset + 1f

        val normalizedScreenSpace = cursorPos.toScreenSpace() ?: return

        val sss = MainTool.platformDependencies.screenSettingsSupplier
        val screenSpace = Vector2f(normalizedScreenSpace.x * sss.width, normalizedScreenSpace.y * sss.height)

        UiElementHelper.drawUiElement(
            lookup = "anc     anc_l   ",
            index = UiElementHelper.currentCursorIndex(6),
            position = screenSpace,
            scale = UiElementHelper.globalUiScale,
            disableGlobalScale = true,
        )
    }

    private fun drawSkillSpeculation() {
        val currentSpeculation = skillSpeculation ?: return
        skillSpeculation = null

        if (context !is TacticsSkillTargetSelect) { return }
        val currentTarget = match.getHighlightedUnit() ?: return

        TacticsSkillSpeculation.draw(currentTarget, currentSpeculation)
    }

    private fun drawUnitSummary() {
        val highlighted = match.getHighlightedUnit()

        if (highlighted != null) {
            TacticsUnitDescription.drawUnitDescription(highlighted.getUnit(), highlighted.type, state = highlighted)
            return
        }

        val current = match.getCurrentTurnUnit() ?: return
        if (!current.isEnemy()) { return }
        TacticsUnitDescription.drawUnitDescription(current.getUnit(), current.type, state = current)
    }

    private fun getCursorWorldPosition(): Vector3f {
        return match.cursorPosition.toWorldSpace() + Vector3f(0f, -1f, 0f)
    }

    private fun snapCursorWorldPosition() {
        worldCursorPosition.copyFrom(getCursorWorldPosition())
    }

    private fun stealCamera(): Boolean {
        stolenCamera = CameraReference.getInstance()

        snapCursorWorldPosition()

        val polarCamera = PolarCamera(
            target = worldCursorPosition,
            targetOffset = Vector3f(),
            screenSettingsSupplier = MainTool.platformDependencies.screenSettingsSupplier,
            minimumRadius = 6f,
            maximumRadius = 6f,
        )

        polarCamera.adjust(desiredCameraPosition * PI/2, PI/1.5)
        polarCamera.forceDisableCollision = true

        CameraReference.setInstance(polarCamera)
        matchCamera = polarCamera

        return true
    }

    private fun returnCamera() {
        val stolen = stolenCamera ?: return
        CameraReference.setInstance(stolen)
        stolenCamera = null
    }

    private fun maybeRemoveRamp() {
        if (removedRamp) { return }
        if (!match.config.isDoneSpawningEnemies()) { return }
        if (match.getUnits().isEmpty() || match.getUnits().any { it.getCursorPosition() is EnemyPrepPosition }) { return }

        removedRamp = true
        SceneManager.getCurrentScene().getMainArea().runEffectRoutine("effe/evyk/pks1")
    }

}

sealed interface TacticsUiContext {

    fun onActivated()

    fun handleEnter()

    fun handleExit()

    fun applyPanelHighlights()

    fun isBoardOnly(): Boolean = true

}

fun toZonePanel(panelPosition: PanelPosition): ZoneObject {
    val objId = panelZoneObjectIdLayout[panelPosition.y][panelPosition.x]
    return SceneManager.getCurrentScene().getMainArea().getZoneResource().zoneObj[objId]
}

fun ActorState.onGainedXp(xp: GainXpResult, context: AttackContext?) {
    val unit = getUnit()

    val unitExpGained = xp.xpGained
    val unitLevelGained = xp.unitLevelGained
    val jobExpGained = xp.jpGained
    val jobLevelGained = xp.jobLevelGained

    AttackContext.compose(context) {
        if (unitExpGained > 0 && jobExpGained > 0) {
            ChatLog("$name gained ${unitExpGained}XP and ${jobExpGained}JP!", ChatLogColor.Info)
        } else if (unitExpGained > 0) {
            ChatLog("$name gained ${unitExpGained}XP!", ChatLogColor.Info)
        } else if (jobExpGained > 0) {
            ChatLog("$name gained ${jobExpGained}JP!", ChatLogColor.Info)
        }

        if (unitExpGained > 0) {
            val scale = 0.8f.interpolate(2f, (unitExpGained/100f).coerceAtMost(1f))
            DamageTextManager.append(this, "XP +$unitExpGained", color = ByteColor(0x80, 0x60, 0x00, 0x00), scale = scale)
        }

        if (jobExpGained > 0) {
            val scale = 0.8f.interpolate(2f, (jobExpGained/100f).coerceAtMost(1f))
            DamageTextManager.append(this, "JP +$jobExpGained", color = ByteColor(0x00, 0x60, 0x80, 0x00), scale = scale)
        }

        if (unitLevelGained) {
            ChatLog("$name attains level ${unit.unitLevel.level}!", ChatLogColor.Action)
            MiscEffects.playEffect(actorId = id, effect = MiscEffects.Effect.LevelUp)
        }

        if (jobLevelGained) {
            val jobConfig = unit.getCurrentJobConfig()
            val jobLevel = unit.getCurrentJobLevel().level

            ChatLog("$name attains ${jobConfig.displayName} level ${jobLevel}!", ChatLogColor.Action)
            MiscEffects.playEffect(actorId = id, effect = MiscEffects.Effect.JobPoint)
        }
    }
}

private fun computeScale(position: Vector3f): Float {
    val d = Vector3f.distance(position, CameraReference.getInstance().getPosition())
    return sqrt(6.55f/d).coerceAtMost(1f)
}
